import { NEXA_INTENTS, NEXA_SUGGESTED_PROMPTS, NEXA_WORKSPACE_INTRO } from "../constants";
import NexaMark from "./NexaMark";

const displayName = (name?: string) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : undefined);

interface Props {
  variant: "panel" | "workspace";
  userName?: string;
  contextLabel?: string;
  disabled?: boolean;
  onPrompt: (prompt: string) => void;
  onIntent: (prompt: string) => void;
}

const PromptCard = ({ prompt, disabled, onClick }: { prompt: string; disabled?: boolean; onClick: () => void }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="tw-flex tw-w-full tw-gap-3 tw-rounded-xl tw-border tw-border-[#EAE8F5] tw-bg-[#FAFAFE] tw-px-4 tw-py-3.5 tw-text-left tw-text-label-md tw-leading-relaxed tw-text-[#3B3960] hover:tw-border-[#D9DAF3] hover:tw-bg-[#F3F3FC] disabled:tw-opacity-60"
  >
    <span className="tw-mt-0.5 tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-border tw-border-[#DFE2FC] tw-bg-[#EEF0FE]">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2.6 3.6h10.8v6.8a1.3 1.3 0 0 1-1.3 1.3H6.6L4 14v-2.3a1.3 1.3 0 0 1-1.4-1.3Z" stroke="#4F46E5" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
    </span>
    {prompt}
  </button>
);

const NexaEmptyState = ({ variant, userName, contextLabel, disabled, onPrompt, onIntent }: Props) => {
  const name = displayName(userName);

  if (variant === "panel") {
    return (
      <div className="tw-pt-1">
        <div className="tw-mb-1.5 tw-text-heading-xs tw-font-semibold tw-text-[#1E1B4B]">{name ? `How can I help, ${name}?` : "How can I help?"}</div>
        <div className="tw-mb-4 tw-text-label-sm tw-leading-relaxed tw-text-gray-500">
          {contextLabel ? `I can see the ${contextLabel} you have open.` : "Ask about classifications, duties, origin, export controls or licensing."}
        </div>
        <div className="tw-space-y-2">
          {NEXA_SUGGESTED_PROMPTS.map((prompt) => (
            <PromptCard key={prompt} prompt={prompt} disabled={disabled} onClick={() => onPrompt(prompt)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-pt-6">
      <div className="tw-mb-5">
        <NexaMark size={64} />
      </div>

      <h1 className="tw-mb-3 tw-text-heading-lg tw-font-bold tw-tracking-tight tw-text-[#1E1B4B]">
        {name ? `Hi ${name}, what can I look up for you?` : "Hi, what can I look up for you?"}
      </h1>

      <p className="tw-mb-5 tw-text-label-lg tw-leading-relaxed tw-text-[#5A5677]">{NEXA_WORKSPACE_INTRO}</p>

      <div className="tw-mb-7 tw-flex tw-flex-wrap tw-gap-2">
        {NEXA_INTENTS.map((intent) => (
          <button
            key={intent.label}
            type="button"
            disabled={disabled}
            onClick={() => onIntent(intent.prompt)}
            className="tw-rounded-full tw-border tw-border-[#E6E6F0] tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-label-sm tw-text-[#3B3960] hover:tw-border-[#DFE2FC] hover:tw-bg-[#EEF0FE] disabled:tw-opacity-60"
          >
            {intent.label}
          </button>
        ))}
      </div>

      <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
        {NEXA_SUGGESTED_PROMPTS.map((prompt) => (
          <PromptCard key={prompt} prompt={prompt} disabled={disabled} onClick={() => onPrompt(prompt)} />
        ))}
      </div>
    </div>
  );
};

export default NexaEmptyState;
